import { throttle, ThrottleSettings, debounce, DebounceSettings } from "lodash-es";
import Vue, { ComponentOptions } from "vue";
import { createDecorator } from "vue-class-component";
import { VueClass } from "vue-class-component/lib/declarations";
import { Location, Route } from "vue-router";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { checkRole, checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import store from "@/store";
import emitter from "@/lib/emitter";
import { getDataByCtx } from "@/types";
import { EVENTS } from "@/const";

interface TargetOptions {
  options?: {
    methods: { [k: string]: (...args: any[]) => any };
    mounted: (() => any)[] | undefined;
    beforeDestroy: (() => any)[] | undefined;
    mixins?: (ComponentOptions<Vue> | typeof Vue)[];
    beforeRouteEnter: ((to: Route, from: Route, next: any) => any)[];
  };
}

export const AuthorisedOnly = createDecorator(function(componentOptions, key) {
  if (!componentOptions.methods?.hasOwnProperty(key)) return;
  const defaultMethod = componentOptions.methods[key];
  componentOptions.methods[key] = function(...args) {
    if (this.$store.state.user) {
      return defaultMethod.apply(this, args);
    }
    emitter.emit(EVENTS.SHOW_LOGIN_SIGNUP_MODAL);
  };
});

export const Throttle = (time: number, opt?: ThrottleSettings) =>
  createDecorator((options, key) => {
    if (options && options.methods && options.methods[key]) {
      const orig = options.methods[key];
      options.methods[key] = throttle(orig, time, opt);
    }
  });

export const Debounce = (time: number, opt?: DebounceSettings) =>
  createDecorator((options, key) => {
    if (options && options.methods && options.methods[key]) {
      const orig = options.methods[key];
      options.methods[key] = debounce(orig, time, opt);
    }
  });

export const mixinReroute = (conditionToStay: boolean, location: Location = { name: "home" }) => {
  return Vue.extend({
    beforeRouteEnter(to: Route, from: Route, next: any) {
      if (conditionToStay) {
        next();
      } else {
        next(location);
      }
    },
  });
};

export const VerifiedOnlyPage = <V extends Vue>(location: Location = { name: "home" }) => {
  return <VC extends VueClass<V>>(target: VC & TargetOptions) => {
    return target.mixin(mixinReroute(store.state.verified !== UserVerificationStatus.UNVERIFIED, location)) as VC &
      TargetOptions;
  };
};

export const CheckRolePage = <V extends Vue>(requiredRole: string, location: Location = { name: "home" }) => {
  return <VC extends VueClass<V>>(target: VC & TargetOptions) => {
    const check = checkRole(store.state?.user?.role || "", requiredRole);
    return target.mixin(mixinReroute(check, location)) as VC & TargetOptions;
  };
};

export const CheckAuthoritiesPage = <V extends Vue>(
  requiredAuthorities: string[],
  location: Location = { name: "home" }
) => {
  return <VC extends VueClass<V>>(target: VC & TargetOptions) => {
    const check = checkAuthorities(store.state?.authorities || [], requiredAuthorities);
    return target.mixin(mixinReroute(check, location)) as VC & TargetOptions;
  };
};
export const CheckRole = (requiredRole: string | getDataByCtx<string>) =>
  createDecorator((options, propertyKey) => {
    if (!options.computed) options.computed = {};
    options.computed[propertyKey] = function(this: any) {
      const required = typeof requiredRole === "function" ? requiredRole(this) : requiredRole;
      return checkRole(store.state?.user?.role || "", required);
    };
  });

export const CheckAuthorities = (requiredAuthorities: string[] | getDataByCtx<string[]>) =>
  createDecorator((options, propertyKey) => {
    if (!options.computed) options.computed = {};
    options.computed[propertyKey] = function(this: any) {
      const required = typeof requiredAuthorities === "function" ? requiredAuthorities(this) : requiredAuthorities;
      return checkAuthorities(store.state.authorities || [], required);
    };
  });

export const CheckFlag = <V extends Vue>(flag: boolean, location: Location = { name: "home" }) => {
  return <VC extends VueClass<V>>(target: VC & TargetOptions) => {
    return target.mixin(mixinReroute(flag, location)) as VC & TargetOptions;
  };
};
